<template>
    <div>
        <!-- 月報 v.1.0
            2019.12 的版本
            更新項目：
                - 預警月曆熱力圖
                - 感測器 K 線圖
                - Air, Sun, Water 改成可以切換裝置
                - 介面 i18n
            刪除項目：
                - 無
        -->
        <!-- 封面區域 -->
        <ReportCover v-if="report_log_time && RData_cropperiod"
                    :report_type="'monthly'"
                    :cropperiod_data="RData_cropperiod"
                    :log_time="report_log_time"
                    @date-month="date_month = $event"
                    @date-range="date_range = $event"
                    @report-url="report_url = $event"
                    @save-name="report_save_name = $event"
        ></ReportCover>


        <div class="print-background">

            <!-- 報告總覽 -->
            <ReportOverview v-if="report_data && RData_cropperiod"
                            :cropperiodData="RData_cropperiod"
                            :deviceInfo="report_data.devices"
                            :growth_period_data="report_data.growth_period"
                            :user="user"
                            :report_url="report_url"
                            :report_save_name="report_save_name"
            ></ReportOverview>


            <!-- 氣象預報 -->
            <div id="weather-data-forecasts" class="print-hide-object">
                <div v-if="show_WeatherForecast">
                    <hr>
                    <CWBWeatherForecasts :weather_data="report_data.cwb" :default_showCollapse="false"></CWBWeatherForecasts>
                    <br>
                    <hr>
                </div>
            </div>


            <!-- 縮時攝影 -->
            <div id="report-area-timelapses" class="print-hide-object">
                <ReportTimelapses v-if="report_data && report_data.timelapses"
                                  :data="report_data.timelapses"
                                  :deviceInfo="report_data.devices"
                ></ReportTimelapses>
            </div>


            <!-- 預警月曆熱力圖 -->
            <div id="report-area-alerts-calendar">
                <ReportAlertCalendar    v-if="report_data"
                                        :alertData="report_data.alerts"
                                        :cropperiodData="RData_cropperiod"
                                        :deviceInfo="report_data.devices"
                                        :timeData="report_data.log_time"
                ></ReportAlertCalendar>
            </div>

            <!-- 資料圖表 -->
            <template v-if="report_data && report_data.data">

                <!-- AIR -->
                <Report3UnitsAir v-if="report_data.data.air"
                                    :deviceData="report_data.data.air"
                                    :deviceInfo="report_data.devices"
                                    :timeData="report_data.log_time"
                ></Report3UnitsAir>

                <!-- SUN -->
                <Report3UnitsSun v-if="report_data.data.sun"
                                    :deviceData="report_data.data.sun"
                                    :deviceInfo="report_data.devices"
                                    :timeData="report_data.log_time"
                ></Report3UnitsSun>

                <!-- WATER -->
                <Report3UnitsWater v-if="report_data.data.water"
                                    :deviceData="report_data.data.water"
                                    :deviceInfo="report_data.devices"
                                    :timeData="report_data.log_time"
                ></Report3UnitsWater>

                <!-- Volatiles 揮發物 -->
                <Report3UnitsVolatiles v-if="report_data.data.volatile_material"
                                    :deviceData="report_data.data.volatile_material"
                                    :deviceInfo="report_data.devices"
                                    :timeData="report_data.log_time"
                ></Report3UnitsVolatiles>

                <!-- DEVICE SWITCH -->
                <ReportDevicesCharts
                                    :deviceData="report_data.data"
                                    :deviceInfo="report_data.devices"
                                    :timeData="report_data.log_time"
                ></ReportDevicesCharts>

            </template>

            <!-- Comment -->
            <div class="report-area print-hide-object">
                <div class="report-area-title-tag" id="report-area-comment">{{ $t('report.ui.comment') }}</div>
                <div class="report-area-card">
                    <ReportComment :page_type="'report'" :page_id="getReportID" :user="user" :timezone="timezone"></ReportComment>
                </div>
            </div>

            <!-- Feedback -->
            <div class="report-area print-hide-object">
                <div class="report-area-title-tag" id="report-area-feedback">{{ $t('menu.feedback_to_agri') }}</div>
                <div class="report-area-card">
                    <div class="report-area">
                        <b-button variant="success" size=lg :href="feedback_link" target="blank">
                            <i class="fa fa-send"></i>
                            {{ $t('menu.feedback_to_agri') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- 放在最下方 -->
            <ButtonTopRight :dropdown="dropdownList" :offsetY="-90"></ButtonTopRight>

        </div>

    </div>
</template>


<script>
import { mapState }     from 'vuex'
import { dragscroll }   from 'vue-dragscroll'  // 滑鼠可拖拉 scroll 區域
import ecTools          from '@/echarts/echarts-tools'
import MixChartMultipleParams from '@/views/echarts/MixChart_MultipleParams'
import MixChartMinMaxParamData from '@/views/echarts/MixChart_MinMaxParamData'
import CWBWeatherForecasts  from '@/views/project/report/components/CWB_WeatherForecasts'
import ReportCover          from '@/views/project/report/components/ReportCover'
import ReportOverview       from '@/views/project/report/components/ReportOverview'
import ReportAlertCalendar  from '@/views/project/report/components/ReportAlertCalendar'
import ReportTimelapses     from '@/views/project/report/components/Report_Timelapses'
import Report3UnitsAir      from '@/views/project/report/components/Report_3Units_Air'
import Report3UnitsSun      from '@/views/project/report/components/Report_3Units_Sun'
import Report3UnitsWater    from '@/views/project/report/components/Report_3Units_Water'
import Report3UnitsVolatiles    from '@/views/project/report/components/Report_3Units_Volatiles'
import ReportDevicesCharts  from '@/views/project/report/components/Report_Charts_switched_by_Devices'
import ReportComment        from '@/views/project/report/components/ReportComment'
import ButtonTopRight       from '@/views/components/Button_TopRight'

export default {
    props: {
        report_data: {
            type: Object,
            require: true,
        },
        user: {
            type: Object,
            default: undefined,
        },
        timezone: {
            type: String,
            default: undefined,
        },
    },
    directives: {
        'dragscroll': dragscroll,
    },
    components: {
        ReportCover,
        CWBWeatherForecasts,
        ReportOverview,
        ReportAlertCalendar,
        ReportTimelapses,
        Report3UnitsAir,
        Report3UnitsSun,
        Report3UnitsWater,
        Report3UnitsVolatiles,
        ReportDevicesCharts,
        MixChartMultipleParams,
        MixChartMinMaxParamData,
        ReportComment,
        ButtonTopRight,
    },
    data () {
        return {
            date_month: '',
            date_range: '',
            report_url: '',
            report_save_name: '',
            report_log_time: [],

            RData_cropperiod: undefined,
            RData_weatherboxs: [],
            RData_devices: [],
        }
    },
    mounted ()
    {
        if (this.timezone == undefined) this.timezone = this.$tools.date.getTimezoneNow();
        if (this.report_data != undefined)
        {
            this.getReport()
        }
    },
    computed: {
        ...mapState({
            'user_language': state => state.user.language,
        }),

        feedback_link ()
        {
            if ( this.user_language == 'zh-Hant-TW' ) return 'https://docs.google.com/forms/d/e/1FAIpQLSct1Wm-Sf-ePaxkNS5_xNzpYvP3zMvB62RcW4n4jFEJLwNtRw/viewform'
            return 'https://reurl.cc/xZ5b2N'
        },

        show_WeatherForecast ()
        {
            if ( this.report_data && this.report_data.cwb )
            {
                if ( this.RData_cropperiod && this.RData_cropperiod.weather_location == 'TW' )
                {
                    return true
                }
            }
            return false
        },

        getReportID ()
        {
            if ( this.$route.query && this.$route.query.id )
                return this.$route.query.id

            else if ( this.$route.query && this.$route.query.ids )
                return this.$route.query.ids

            else undefined
        },

        dropdownList ()
        {
            let _list = [
                { icon: 'fa fa-arrow-up', name_code: 'go_top', name: 'Top', href: '#page-top', },
                { icon: 'fa fa-book', name: 'Overview', href: '#page-overview', },
            ]
            if (this.report_data)
            {
                if (this.report_data.cwb)
                {
                    _list.push( { icon: 'wi wi-raindrop', name_code: 'weather_forecast', name: 'Forecast', href: '#weather-data-forecasts' } )
                }
                if (this.report_data.timelapses && this.report_data.timelapses.length > 0)
                {
                    _list.push( { icon: 'fa fa-camera', name_code: 'time_lapse_photo', name: 'Time-lapses', href: '#report-area-timelapses' } )
                }
                if (this.report_data.alerts)
                {
                    _list.push( { icon: 'fa fa-bullhorn', name_code: undefined, name: this.$t('report.ui.alert_calendar'), href: '#report-area-alerts-calendar' } )
                }
                if ( this.report_data.data )
                {
                    if ( this.report_data.data.air && Object.keys(this.report_data.data.air).length > 0 )
                    {
                        _list.push( { icon: this.$tools.style.classIcon('weatherbox'), name: this.$t('report.ui.air'), href: '#report-area-air', } )
                    }

                    if ( this.report_data.data.sun && Object.keys(this.report_data.data.sun).length > 0 )
                    {
                        _list.push( { icon: this.$tools.style.classIcon('weatherbox'), name: this.$t('report.ui.sun'), href: '#report-area-sun', } )
                    }
                    if ( this.report_data.data.water && Object.keys(this.report_data.data.water).length > 0 )
                    {
                        _list.push( { icon: this.$tools.style.classIcon('weatherbox'), name: this.$t('report.ui.water'), href: '#report-area-water', } )
                    }
                    if ( this.report_data.data.volatile_material && Object.keys(this.report_data.data.volatile_material).length > 0 )
                    {
                        _list.push( { icon: this.$tools.style.classIcon('weatherbox'), name: this.$t('report.ui.volatiles'), href: '#report-area-volatiles', } )
                    }
                }

                if (this.RData_devices.length > 0)
                {
                    _list.push( {   icon: this.$tools.style.classIcon('wand'),
                                    name: this.$t('menu.sensor_parameter'),
                                    href: '#report-area-sensor_parameter' }, )
                }

                _list.push( { icon: 'fa fa-pencil', name_code: 'comment', name: 'Comment', href: '#report-area-comment' } )

                _list.push( {   icon: 'fa fa-send', name_code: 'feedback_to_agri', name: 'Feedback',
                                type: 'link', href: this.feedback_link } )
            }
            // console.log( _list )

            return _list
        },

    },
    watch: {

        report_data (val, oldVal)
        {
            if (val)
            {
                this.getReport()
            }
        },

    },
    methods: {

        getReport ()
        {
            // 期作 & 專案資訊
            this.RData_cropperiod = this.report_data.cropperiod_meta
            this.RData_cropperiod.start_date = this.$moment( this.RData_cropperiod.start_date ).format('YYYY-MM-DD')
            if (this.RData_cropperiod.end_date) this.RData_cropperiod.end_date = this.$moment( this.RData_cropperiod.end_date ).format('YYYY-MM-DD')


            // 處理裝置資料
            let _device_data = this.report_data.devices

            this.RData_devices = []
            this.RData_weatherboxs = []

            for ( let _deviceHashID in _device_data )
            {
                let _device = _device_data[ _deviceHashID ]


                _device.hashid = _deviceHashID

                if ( _device.type == 'weatherbox' )
                {
                    this.RData_weatherboxs.push( _device )
                }
                else if ( _device.type == 'wand' )
                {
                    this.RData_devices.push( _device )
                }

            }

            // 紀錄日期時間 array
            if ( this.report_data.log_time && this.report_data.log_time.day )
            {
                this.report_log_time = this.report_data.log_time.day
            }
            else
            {
                this.report_log_time = []
            }


        },

    }
}
</script>

<style lang="scss" scoped>

</style>
